import { createSelector } from "reselect"
import { get, find, chain, map, filter, first, mergeWith, some, size, pick } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getAllAvailableRollups } from "helpers/rollups"
import { positionSetContributorsSummaryText, sortContributors } from "helpers/contributors"
import { makeGroupSort } from "helpers/contributorsSort"
import { getScreenName } from "modules/analyze-composite-factor-sensitivity/selectors/file"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { aggregateExposuresFactors } from "helpers/factorClassification"
import { computeSelectedGroup } from "helpers/analyze"
import { createQueryStringSelector } from "selectors/queryString"

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []

export const getGroupBy = (_, props) => get(props, "currentGrouping.id")
export const getSelectedContributorCategory = (_, props) => props.selectedContributorCategory
const getPositionSetType = (_, props) => props.positionSetType
const getAggregationFunction = (_, props) => props.aggregation
const getMustAggregateFactors = (_, props) => props.mustAggregateFactors
const getFactorsClassification = (_, props) => props.factorsClassification
const getCurrentGrouping = (_, props) => get(props, "currentGrouping")
const getPositionSetName = (_, props) => props.positionSetName
const getClassifications = (_, props) => props.classifications
const getContributorsData = state => get(state, "global.data.compositeFactorSensitivityContributors.data.model.simulation.factorSensitivityContributors")
const getGroupedContributorsData = state => get(state, "global.data.compositeGroupedFactorSensitivityContributors.data.model.simulation.groupedFactorSensitivityContributors")
const getSelectedGroupId = createQueryStringSelector("contributorGroup")
const getOrder = (_, props) => get(props, "sortOrder", EMPTY_OBJECT)
const getSelectedDate = (_, props) => props.selectedDate
const getShowNotHeld = (_, props) => props.showNotHeld
const getViewType = (_, props) => props.viewType
const getSelectedViewsFactors = (_, props) => props.selectedViewsFactors
const getIsActive = (_, props) => props.isActive

function getFactors(factors, mustAggregateFactors, styleAggregation, selectedViewFactors, factorsClassification) {
  if (!mustAggregateFactors) {
    return factors
  }

  let aggregatedFactors = aggregateExposuresFactors(factorsClassification, factors, styleAggregation, "sensitivity")
  if (size(selectedViewFactors)) {
    aggregatedFactors = chain(factors)
      .filter(factor => some(selectedViewFactors, item => item.id === factor.id))
      .concat(aggregatedFactors)
      .value()
  }
  return aggregatedFactors
}

function mapFactors(factors) {
  return chain(factors)
    .keyBy("id")
    .mapValues(f => f.sensitivity * 100)
    .value()
}

function computeNotHeldBucket(contributors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification) {
  const merge = (obj, source) => mergeWith(obj, source, (objValue, srcValue) =>
    !objValue ? srcValue : objValue + srcValue,
  )

  return chain(contributors)
    .filter(item => !!item.notHeld)
    .reduce((acc, value) => {
      acc.size += value.percentEquity
      const factors = getFactors(value.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification)
      merge(acc.factors, mapFactors(factors))

      return acc
    }, {
      id: "NOT HELD",
      notHeld: true,
      size: 0,
      factors: {},
    })
    .value()
}

function mapFactorSensitivityContributors(data, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, isActive) {
  const result = chain(data)
    .filter(item => showNotHeld || !item.notHeld)
    .map(security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
      const factors = getFactors(security.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification)
      return {
        ...pick(security, ["id", "country", "description", "currency", "assetClass", "assetSubclass", "notHeld", "bloombergTicker", "modelProviderId"]),
        classification: get(security, "classification.name"),
        size: security.percentEquity,
        factors: mapFactors(factors),
        ticker,
        exchange,
      }
    })
    .value()

  if (isActive && !showNotHeld) {
    result.push(computeNotHeldBucket(data, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification))
  }
  return result
}

export const getSelectedGroup = createSelector(
  [getSelectedGroupId, getGroupedContributorsData],
  (selectedGroupId, contributors) => computeSelectedGroup({ id: selectedGroupId }, contributors),
)

export const getIncludeNotHeld = createSelector(
  [getIsActive],
  (isActive) => isActive,
)

const getSelectedField = createSelector(
  [getSelectedContributorCategory],
  (selected) => get(selected, "field", "total"),
)

const getContributors = createSelector(
  [getContributorsData, getShowNotHeld, getMustAggregateFactors, getAggregationFunction, getSelectedViewsFactors, getFactorsClassification, getIsActive],
  mapFactorSensitivityContributors,
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) =>
    filter(data, security => get(security, selectedField)),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY
    return sortContributors(data, selectedField, order)
  },
)

const getGroupedContributors = createSelector(
  [getGroupedContributorsData, getShowNotHeld, getMustAggregateFactors, getAggregationFunction, getSelectedViewsFactors, getFactorsClassification, getIsActive],
  (data, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, isActive) =>
    map(data, group => {
      const factors = getFactors(group.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification)
      return {
        ...group,
        factors: chain(factors)
          .keyBy("id")
          .mapValues(f => f.sensitivity * 100)
          .value(),
        contributors: mapFactorSensitivityContributors(group.contributors, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, isActive),
      }
    }),
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  (data, selectedField, selectedGroup) => chain(data)
    .filter(group => !selectedGroup || selectedGroup.id === group.id)
    .map(group => ({
      ...group,
      contributors: filter(group.contributors, security => get(security, selectedField)),
    }))
    .value(),
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return chain(data)
      .map(group => ({
        ...group,
        value: get(group, selectedField),
        contributors: sortContributors(group.contributors, selectedField, order),
      }))
      .orderBy(...makeGroupSort(order))
      .value()
  },
)

const getAccountRollups = createSelector(
  [getClassifications],
  (classifications) => ({
    classifications,
  }),
)
export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate, getViewType],
  getAllAvailableRollups,
)

export const getContributorsSummaryText = createSelector(
  [getCurrentGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup, getShowNotHeld, getViewType, getPositionSetType],
  positionSetContributorsSummaryText,
)

export const getExcelData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelectedContributorCategory, getCurrentGrouping, getPositionSetName, getSelectedDate, getViewType, getSelectedGroup, getScreenName],
  (data, groupedData, selectedContributorCategory, contributorsGrouping, positionSetName, selectedDate, viewType, selectedGroup, screenName) => {
    const defaultOptions = {
      type: screenName,
      portfolioName: positionSetName,
      selected: selectedContributorCategory,
      selectedDate,
      viewType,
      net: true,
    }

    if (selectedGroup) {
      const group = first(groupedData)
      return computeGroupedContributorsCSV({
        ...defaultOptions,
        group,
      })
    }

    if (contributorsGrouping) {
      return computeContributorsGroupCSV({
        ...defaultOptions,
        contributorsGrouping,
        data: groupedData,
      })
    }

    return computeContributorsCSV({
      ...defaultOptions,
      data,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      const group = find(groupedContributors, { id: selectedGroup.id })
      return get(group, selectedField)
    }

    return null
  },
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      return chain(groupedContributors)
        .map(group => ({
          ...group,
          value: get(group, selectedField),
        }))
        .orderBy(["name"], ["asc"])
        .value()
    }

    return null
  },
)
